import { Toaster } from "react-hot-toast";
import { BrowserRouter as Router, Link } from "react-router-dom";
import AppRoutes from "./routes/AppRoutes";
import React, { useContext, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ItemsContext } from "./data/context";
import { Cookie } from "lucide-react";
import ScrollToTop from "./Hooks/useNavigateToTop";
import { StickyContact } from "./component/ui";
import Hotjar from "@hotjar/browser";
import { initGA4 } from "./utils/analytics/ga4";

function App() {
  const { isPolicyModel, setIsPolicyModelOpen } = useContext(ItemsContext);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const policy = localStorage.getItem("fownd-policy");

    if (policy === "true") {
      const siteId = 3714868;
      const hotjarVersion = 6;
      Hotjar.init(siteId, hotjarVersion);
      console.log("Recording Analytics...");
    }

    if (policy === "false") {
      setIsPolicyModelOpen(false);
      initGA4();
    }
  }, [setIsPolicyModelOpen]);

  const rejectCookies = () => {
    localStorage.setItem("fownd-policy", "true");
    setIsPolicyModelOpen(false);
  };

  const acceptCookies = () => {
    localStorage.setItem("fownd-policy", "false");
    setIsPolicyModelOpen(false);
    initGA4();
  };

  return (
    <>
      <Router>
        <ScrollToTop />
        {isPolicyModel && (
          <Transition appear show={isPolicyModel} as={React.Fragment}>
            <Dialog
              as="div"
              className="relative z-[80]"
              onClose={() => setShowError(true)}
              static
            >
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-[#1F3D3B]/35 backdrop-blur-[2px]" />
              </Transition.Child>

              {/* Above mobile bottom nav (z-62); clear of the bar on small screens */}
              <div className="fixed inset-x-0 bottom-0 z-[80] flex justify-center p-3 pb-[5.75rem] md:inset-auto md:bottom-6 md:right-6 md:left-auto md:p-0 md:pb-0">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
                  enterTo="opacity-100 translate-y-0 md:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 md:scale-100"
                  leaveTo="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-[0_24px_64px_-16px_rgba(31,61,59,0.45)] ring-1 ring-black/5">
                    <div className="h-2.5 w-full bg-[#1F3D3B]" />

                    <div className="p-5 sm:p-6">
                      <div className="flex items-start gap-3.5">
                        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#1F3D3B]/[0.06] text-[#1F3D3B]">
                          <Cookie className="h-5 w-5" strokeWidth={1.75} />
                        </span>
                        <div className="min-w-0 flex-1 text-left">
                          <Dialog.Title className="font-productSans text-lg sm:text-xl font-bold text-gray-900 leading-snug">
                            We use{" "}
                            <span className="text-[#486633]">cookies</span> to
                            improve your experience
                          </Dialog.Title>
                          <Dialog.Description className="mt-2 font-productSans text-sm text-gray-600 leading-relaxed">
                            This site uses session cookies and similar tracking
                            technologies. You can opt in or opt out anytime. See
                            our{" "}
                            <Link
                              to="/cookie-policy"
                              className="font-semibold text-[#619ed3] hover:text-[#3d7aa8] underline-offset-2 hover:underline"
                            >
                              Cookie Policy
                            </Link>
                            .
                          </Dialog.Description>
                        </div>
                      </div>

                      {showError && (
                        <p className="mt-3 text-center font-productSans text-sm font-semibold text-red-600">
                          Please choose an option to continue.
                        </p>
                      )}

                      <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5">
                        <button
                          type="button"
                          onClick={rejectCookies}
                          className="inline-flex h-11 items-center justify-center rounded-full border border-gray-200 bg-white px-5 font-productSans text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#619ed3]/40"
                        >
                          Reject All
                        </button>
                        <button
                          type="button"
                          onClick={acceptCookies}
                          className="inline-flex h-11 items-center justify-center rounded-full bg-gradient-to-r from-[#619ed3] to-[#5c91a4] px-5 font-productSans text-sm font-semibold text-white shadow-md shadow-[#619ed3]/25 transition-all hover:from-[#3a8bb8] hover:to-[#4f8291] hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#619ed3]/50 focus-visible:ring-offset-2"
                        >
                          Accept All
                        </button>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>
        )}
        <AppRoutes />
        {typeof window !== "undefined" &&
          localStorage.getItem("fownd-policy") !== null &&
          !isPolicyModel && <StickyContact />}
        <Toaster position="bottom-center" />
      </Router>
    </>
  );
}

export default App;
